import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, updateDoc, doc, deleteDoc, getDocs, serverTimestamp, setDoc, writeBatch, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { generateTimestampId } from '../../utils/documentIdUtils';
import { useAuth } from '../auth/AuthContext';
import Toast from './Toast';

const functions = getFunctions();

// Available reports for subscription
export const AVAILABLE_REPORTS = [
  { id: 'order_summary', name: 'Order Summary Report', description: 'Summary of orders by date range', supportsDateRange: true },
  { id: 'customer_inactivity', name: 'Customer Inactivity Report', description: 'Customers with no orders in 365+ days', supportsDateRange: false },
  { id: 'sales_rep_performance', name: 'Sales Rep Performance', description: 'Revenue and order metrics by sales rep', supportsDateRange: true },
  { id: 'opportunity_pipeline', name: 'Opportunity Pipeline', description: 'Current pipeline status and forecast', supportsDateRange: false },
  { id: 'task_summary', name: 'Task Summary', description: 'Your pending and overdue tasks (assigned to or created by you)', supportsDateRange: false }
];

const DATE_RANGE_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' }
];

const MONTHLY_ONLY_REPORT_IDS = new Set(['customer_inactivity']);

const isMonthlyOnlyReport = (reportId) => MONTHLY_ONLY_REPORT_IDS.has(reportId);

const normalizeScheduleForReport = (reportId, schedule = {}) => {
  if (!isMonthlyOnlyReport(reportId)) return schedule;
  return {
    ...schedule,
    cadence: 'monthly',
    day: String(schedule.day || '1')
  };
};

/**
 * ReportSubscriptionsPanel - Reusable component for managing email report subscriptions
 * Can be used in NotificationsPage or UserProfilePage
 */
const ReportSubscriptionsPanel = ({ showHeader = true }) => {
  const { currentUser } = useAuth();
  
  // Toast for this component
  const [toast, setToast] = useState(null);
  
  // Email Subscriptions state
  const [reportSubscriptions, setReportSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [savingSubscription, setSavingSubscription] = useState(false);
  
  // Report-specific custom schedule state (keyed by report ID)
  const [reportCustomStates, setReportCustomStates] = useState({});
  
  // Track which subscription is being edited
  const [editingSubscription, setEditingSubscription] = useState(null);
  
  // Global custom schedule for "Apply to All"
  const [globalCustomSchedule, setGlobalCustomSchedule] = useState({ cadence: 'daily', day: 'monday', time: '07:00' });
  const [showGlobalCustomSchedule, setShowGlobalCustomSchedule] = useState(false);
  
  // History and preview state
  const [sendHistory, setSendHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryReport, setSelectedHistoryReport] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [previewingReport, setPreviewingReport] = useState(null);
  const [userTimezone, setUserTimezone] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  
  // Rate limiting for "Send Immediately"
  const [sendTimestamps, setSendTimestamps] = useState({});

  // Detect user's timezone on mount
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezone(tz);
    } catch (err) {
      console.error('Error detecting timezone:', err);
      setUserTimezone('America/New_York');
    }
  }, []);

  // Fetch report subscriptions with real-time updates
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    setLoadingSubscriptions(true);
    const q = query(
      collection(db, collectionPath('reportSubscriptions')),
      where('userId', '==', currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const subs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setReportSubscriptions(subs);
        setLoadingSubscriptions(false);
      },
      (err) => {
        console.warn('Failed to load report subscriptions:', err);
        setLoadingSubscriptions(false);
      }
    );
    
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Helper function to format time from 24-hour to 12-hour format
  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Create a new subscription variant
  const createSubscriptionVariant = async (reportId, customScheduleData) => {
    if (!currentUser?.uid || savingSubscription) return;
    
    setSavingSubscription(true);
    try {
      const normalizedSchedule = normalizeScheduleForReport(reportId, customScheduleData);
      const newSub = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        reportId,
        frequency: 'custom',
        timezone: userTimezone || 'America/New_York',
        dateRange: normalizedSchedule.dateRange || null,
        customSchedule: normalizedSchedule,
        createdAt: serverTimestamp(),
        isActive: true
      };
      
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const dateRangeSuffix = customScheduleData.dateRange ? `_${customScheduleData.dateRange}d` : '';
      const docId = `reportsub_${currentUser.uid}_${reportId}${dateRangeSuffix}_${timestamp}_${randomSuffix}`;
      
      await setDoc(doc(db, collectionPath('reportSubscriptions'), docId), newSub);
      // onSnapshot listener handles state update — no optimistic update needed
      setToast({ message: 'Subscription variant created', type: 'success' });
    } catch (err) {
      console.error('Failed to create subscription variant:', err);
      setToast({ message: 'Failed to create variant: ' + err.message, type: 'error' });
    } finally {
      setSavingSubscription(false);
    }
  };

  // Toggle report subscription
  const toggleReportSubscription = async (reportId, frequency, customScheduleData = null) => {
    if (!currentUser?.uid || savingSubscription) return;
    
    setSavingSubscription(true);
    try {
      const existingSub = reportSubscriptions.find(s => s.reportId === reportId);
      
      if (existingSub) {
        if (frequency === 'none') {
          await deleteSubscription(existingSub.id);
        } else {
          const normalizedSchedule = customScheduleData ? normalizeScheduleForReport(reportId, customScheduleData) : null;
          const updateData = {
            frequency,
            updatedAt: serverTimestamp()
          };
          if (frequency === 'custom' && normalizedSchedule) {
            updateData.customSchedule = normalizedSchedule;
            if (normalizedSchedule.dateRange) {
              updateData.dateRange = normalizedSchedule.dateRange;
            }
          }
          updateData.timezone = userTimezone || 'America/New_York';
          await updateDoc(doc(db, collectionPath('reportSubscriptions'), existingSub.id), updateData);
          // onSnapshot listener handles state update
        }
      } else if (frequency !== 'none') {
        const normalizedSchedule = customScheduleData ? normalizeScheduleForReport(reportId, customScheduleData) : null;
        const newSub = {
          userId: currentUser.uid,
          userEmail: currentUser.email,
          reportId,
          frequency,
          timezone: userTimezone || 'America/New_York',
          dateRange: normalizedSchedule?.dateRange || null,
          createdAt: serverTimestamp(),
          isActive: true
        };
        if (frequency === 'custom' && normalizedSchedule) {
          newSub.customSchedule = normalizedSchedule;
        }
        const dateRangeSuffix = normalizedSchedule?.dateRange ? `_${normalizedSchedule.dateRange}d` : '';
        const docId = generateTimestampId(`reportsub_${currentUser.uid}_${reportId}${dateRangeSuffix}`);
        await setDoc(doc(db, collectionPath('reportSubscriptions'), docId), newSub);
        // onSnapshot listener handles state update — no optimistic update needed
      }
      setToast({ message: 'Subscription updated', type: 'success' });
    } catch (err) {
      console.error('Failed to update subscription:', err);
      setToast({ message: 'Failed to update subscription: ' + err.message, type: 'error' });
    } finally {
      setSavingSubscription(false);
    }
  };

  // Delete subscription and its history
  const deleteSubscription = async (subscriptionId) => {
    if (!subscriptionId || !currentUser?.uid) return;
    
    try {
      await deleteDoc(doc(db, collectionPath('reportSubscriptions'), subscriptionId));
      
      const historyQuery = query(
        collection(db, collectionPath('reportSendHistory')),
        where('subscriptionId', '==', subscriptionId)
      );
      const historySnap = await getDocs(historyQuery);
      
      if (!historySnap.empty) {
        const batch = writeBatch(db);
        historySnap.docs.forEach(histDoc => {
          batch.delete(histDoc.ref);
        });
        await batch.commit();
      }
      
      setReportSubscriptions(prev => prev.filter(s => s.id !== subscriptionId));
      setToast({ message: 'Subscription and history deleted', type: 'success' });
    } catch (err) {
      console.error('Failed to delete subscription:', err);
      setToast({ message: 'Failed to delete subscription: ' + err.message, type: 'error' });
    }
  };

  // Pause/Resume subscription
  const togglePauseSubscription = async (reportId, isPaused, subscriptionId = null) => {
    const existingSub = subscriptionId 
      ? reportSubscriptions.find(s => s.id === subscriptionId)
      : reportSubscriptions.find(s => s.reportId === reportId);
    if (!existingSub) return;
    
    try {
      await updateDoc(doc(db, collectionPath('reportSubscriptions'), existingSub.id), {
        isPaused,
        pauseReason: isPaused ? 'Manually paused by user' : null,
        updatedAt: serverTimestamp()
      });
      setReportSubscriptions(prev => prev.map(s =>
        s.id === existingSub.id ? { ...s, isPaused, pauseReason: isPaused ? 'Manually paused by user' : null } : s
      ));
      setToast({ message: isPaused ? 'Subscription paused' : 'Subscription resumed', type: 'success' });
    } catch (err) {
      console.error('Failed to toggle pause:', err);
      setToast({ message: 'Failed to update subscription', type: 'error' });
    }
  };

  // Fetch send history
  const fetchSendHistory = async (reportId = null, subscriptionId = null) => {
    setLoadingHistory(true);
    try {
      let q = query(
        collection(db, collectionPath('reportSendHistory')),
        where('userId', '==', currentUser.uid),
        orderBy('sentAt', 'desc')
      );
      
      if (subscriptionId) {
        q = query(
          collection(db, collectionPath('reportSendHistory')),
          where('userId', '==', currentUser.uid),
          where('subscriptionId', '==', subscriptionId),
          orderBy('sentAt', 'desc')
        );
      } else if (reportId) {
        q = query(
          collection(db, collectionPath('reportSendHistory')),
          where('userId', '==', currentUser.uid),
          where('reportId', '==', reportId),
          orderBy('sentAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      const history = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSendHistory(history);
      setShowHistory(true);
      setSelectedHistoryReport(reportId);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setToast({ message: 'Failed to load send history', type: 'error' });
    } finally {
      setLoadingHistory(false);
    }
  };

  // Calculate remaining sends today and cooldown time (per-report)
  const getRateLimitInfo = (reportId) => {
    const now = Date.now();
    const tenMinutesAgo = now - 10 * 60 * 1000;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    const reportTimestamps = sendTimestamps[reportId] || [];
    const recentSends = reportTimestamps.filter(ts => ts > tenMinutesAgo);
    const todaysSends = reportTimestamps.filter(ts => ts > todayStart.getTime());
    
    const onCooldown = recentSends.length > 0;
    const minutesRemaining = onCooldown ? Math.ceil((10 * 60 * 1000 - (now - Math.max(...recentSends))) / 60000) : 0;
    const sendsRemaining = 4 - todaysSends.length;
    
    return { onCooldown, minutesRemaining, sendsRemaining };
  };

  // Send report immediately
  const sendReportNow = async (reportId, dateRange = null) => {
    const now = Date.now();
    const tenMinutesAgo = now - 10 * 60 * 1000;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    const reportTimestamps = sendTimestamps[reportId] || [];
    const recentSends = reportTimestamps.filter(ts => ts > tenMinutesAgo);
    const todaysSends = reportTimestamps.filter(ts => ts > todayStart.getTime());
    
    if (recentSends.length > 0) {
      const lastSend = Math.max(...recentSends);
      const minutesSinceLast = Math.round((now - lastSend) / 60000);
      const minutesRemaining = 10 - minutesSinceLast;
      setToast({ 
        message: `Please wait ${minutesRemaining} minute${minutesRemaining > 1 ? 's' : ''} before sending this report again`, 
        type: 'warning' 
      });
      return;
    }
    
    if (todaysSends.length >= 4) {
      setToast({ 
        message: 'Daily limit reached for this report (4 per day). Try again tomorrow.', 
        type: 'warning' 
      });
      return;
    }
    
    setPreviewingReport(reportId);
    setPreviewing(true);
    setToast({ message: '📊 Generating report... This may take a few seconds.', type: 'info' });
    
    try {
      const testFunction = httpsCallable(functions, 'testReportSubscriptions');
      const params = { 
        email: currentUser.email,
        reportId,
        sendImmediate: true
      };
      if (dateRange) {
        params.dateRange = dateRange;
      }
      await testFunction(params);
      
      setSendTimestamps(prev => ({
        ...prev,
        [reportId]: [...(prev[reportId] || []), now].slice(-10)
      }));
      
      setToast({ message: `✅ Report sent to ${currentUser.email}!`, type: 'success' });
    } catch (err) {
      console.error('Send failed:', err);
      setToast({ message: 'Failed to send report: ' + err.message, type: 'error' });
    } finally {
      setPreviewingReport(null);
      setPreviewing(false);
    }
  };

  return (
    <div className="space-y-4">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      {showHeader && (
        <div className="mb-4">
          <h3 className="text-xl font-semibold">📧 Email Subscriptions</h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure email notifications and report subscriptions. Emails are sent to your account email.
          </p>
          {userTimezone && (
            <p className="text-xs text-indigo-600 mt-2">
              🌍 Your timezone is: <span className="font-mono font-semibold">{userTimezone}</span> - All scheduled times are in your local timezone
            </p>
          )}
        </div>
      )}

      {/* Report Subscriptions Section */}
      <div>
        <div className="mb-3">
          <h4 className="font-medium text-gray-900">Report Subscriptions</h4>
          <p className="text-sm text-gray-600 mt-1">
            Subscribe to receive reports via email on a schedule. Includes Task Summary for your pending/overdue tasks.
          </p>                
        </div>
        
        {loadingSubscriptions ? (
          <div className="text-gray-500 text-center py-4">Loading subscriptions...</div>
        ) : (
          <div className="space-y-3">
            {/* Global Custom Schedule Panel */}
            {showGlobalCustomSchedule && (
              <div className="p-4 bg-indigo-50 border-2 border-indigo-300 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-indigo-900">Set Custom Schedule for All Reports</h5>
                  <button
                    onClick={() => setShowGlobalCustomSchedule(false)}
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    ✕
                  </button>
                </div>
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Cadence</label>
                    <select
                      value={globalCustomSchedule.cadence}
                      onChange={(e) => setGlobalCustomSchedule(prev => ({ ...prev, cadence: e.target.value }))}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  {globalCustomSchedule.cadence === 'weekly' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Day of Week</label>
                      <select
                        value={globalCustomSchedule.day}
                        onChange={(e) => setGlobalCustomSchedule(prev => ({ ...prev, day: e.target.value }))}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                    </div>
                  )}
                  {globalCustomSchedule.cadence === 'monthly' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Day of Month</label>
                      <select
                        value={globalCustomSchedule.day || '1'}
                        onChange={(e) => setGlobalCustomSchedule(prev => ({ ...prev, day: e.target.value }))}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Time (15-min intervals)</label>
                    <input
                      type="time"
                      step="900"
                      value={globalCustomSchedule.time}
                      onChange={(e) => setGlobalCustomSchedule(prev => ({ ...prev, time: e.target.value }))}
                      className="border rounded px-2 py-1 text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      const confirmed = window.confirm(
                        '⚠️ Warning: This will update ALL existing report subscriptions to use the same schedule time.\n\n' +
                        'Any date range variants you have created will also be updated to this schedule.\n\n' +
                        'Are you sure you want to continue?'
                      );
                      if (!confirmed) return;
                      
                      setSavingSubscription(true);
                      try {
                        for (const report of AVAILABLE_REPORTS) {
                          const scheduleForReport = normalizeScheduleForReport(report.id, globalCustomSchedule);
                          await toggleReportSubscription(report.id, 'custom', scheduleForReport);
                        }
                        setToast({ message: 'Custom schedule applied to all reports', type: 'success' });
                        setShowGlobalCustomSchedule(false);
                      } catch (err) {
                        setToast({ message: 'Failed to apply schedule: ' + err.message, type: 'error' });
                      } finally {
                        setSavingSubscription(false);
                      }
                    }}
                    disabled={savingSubscription}
                    className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {savingSubscription ? 'Applying...' : 'Apply to All Reports'}
                  </button>
                  <button
                    onClick={() => setShowGlobalCustomSchedule(false)}
                    className="px-3 py-1 bg-gray-300 rounded text-sm hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Button to show global custom schedule */}
            {!showGlobalCustomSchedule && (
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => setShowGlobalCustomSchedule(true)}
                  className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 border border-indigo-300"
                >
                  📅 Set Custom Schedule for All Reports
                </button>
              </div>
            )}

            {AVAILABLE_REPORTS.map(report => {
              const subscriptions = reportSubscriptions.filter(s => s.reportId === report.id);
              const hasSubscriptions = subscriptions.length > 0;
              const showReportCustom = reportCustomStates[report.id]?.show || false;
              const defaultSchedule = isMonthlyOnlyReport(report.id)
                ? { cadence: 'monthly', day: '1', time: '07:00', dateRange: report.supportsDateRange ? 7 : null }
                : { cadence: 'daily', day: 'monday', time: '07:00', dateRange: report.supportsDateRange ? 7 : null };
              const reportCustomSchedule = reportCustomStates[report.id]?.schedule || defaultSchedule;
              
              return (
                <div key={report.id} className="p-4 bg-gray-50 rounded-lg border hover:border-indigo-300 transition-colors space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-gray-900">{report.name}</div>
                        {report.supportsDateRange && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded" title="This report supports custom date ranges">
                            📅 Date Range
                          </span>
                        )}
                        {hasSubscriptions && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                            {subscriptions.length} active
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{report.description}</div>
                      
                      {/* Show all subscription variants */}
                      {subscriptions.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {subscriptions.map((sub) => (
                            <div key={sub.id} className="bg-white px-3 py-2 rounded border border-gray-200">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 flex items-center gap-2 flex-wrap">
                                  {sub.dateRange && (
                                    <span className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                                      {sub.dateRange}d
                                    </span>
                                  )}
                                  {sub.customSchedule && (
                                    <span className="text-xs text-gray-600">
                                      📅 {sub.customSchedule.cadence === 'daily' ? 'Every day' : sub.customSchedule.cadence === 'weekly' ? `Weekly on ${sub.customSchedule.day.charAt(0).toUpperCase() + sub.customSchedule.day.slice(1)}` : `Monthly on day ${sub.customSchedule.day || 1}`} at {formatTime12Hour(sub.customSchedule.time)}
                                    </span>
                                  )}
                                  {sub.isPaused && (
                                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Paused</span>
                                  )}
                                  {sub.lastSendStatus === 'success' && (
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">✓ Sent</span>
                                  )}
                                  {sub.lastSendStatus === 'failed' && (
                                    <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded" title={sub.lastSendError}>
                                      ⚠️ Failed
                                    </span>
                                  )}
                                  {sub.consecutiveFailures > 0 && (
                                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded" title={`${sub.consecutiveFailures} consecutive failures`}>
                                      {sub.consecutiveFailures} failures
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      setEditingSubscription(sub);
                                      setReportCustomStates(prev => ({
                                        ...prev,
                                        [report.id]: {
                                          show: true,
                                          isNewVariant: false,
                                          schedule: {
                                            cadence: isMonthlyOnlyReport(report.id) ? 'monthly' : (sub.customSchedule?.cadence || 'daily'),
                                            day: isMonthlyOnlyReport(report.id) ? String(sub.customSchedule?.day || '1') : (sub.customSchedule?.day || 'monday'),
                                            time: sub.customSchedule?.time || '07:00',
                                            dateRange: sub.dateRange || (report.supportsDateRange ? 7 : null)
                                          }
                                        }
                                      }));
                                    }}
                                    className="text-s text-blue-600 hover:text-blue-800"
                                    title="Edit subscription schedule"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    onClick={() => togglePauseSubscription(report.id, !sub.isPaused, sub.id)}
                                    className="text-s text-gray-600 hover:text-gray-800"
                                    title={sub.isPaused ? 'Resume' : 'Pause'}
                                  >
                                    {sub.isPaused ? '▶️' : '⏸'}
                                  </button>
                                  <button
                                    onClick={() => sendReportNow(report.id, sub.dateRange)}
                                    disabled={previewing || getRateLimitInfo(report.id).sendsRemaining <= 0}
                                    className={`text-s ${previewing && previewingReport === report.id ? 'text-gray-400 cursor-wait' : getRateLimitInfo(report.id).sendsRemaining <= 0 ? 'text-gray-300 cursor-not-allowed' : 'text-green-600 hover:text-green-800'} disabled:opacity-50`}
                                    title={previewing && previewingReport === report.id ? 'Sending...' : getRateLimitInfo(report.id).onCooldown ? `Cooldown: ${getRateLimitInfo(report.id).minutesRemaining} min remaining` : getRateLimitInfo(report.id).sendsRemaining <= 0 ? 'Daily limit reached (4/day)' : `Send immediately (${getRateLimitInfo(report.id).sendsRemaining} remaining today)`}
                                  >
                                    {previewing && previewingReport === report.id ? '⏳' : '📤'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      fetchSendHistory(report.id, sub.id);
                                      setSelectedHistoryReport(report.id);
                                    }}
                                    className="text-s text-blue-600 hover:text-blue-800"
                                    title="View send history for this subscription"
                                  >
                                    📜
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (window.confirm('Delete this subscription and its send history?')) {
                                        await deleteSubscription(sub.id);
                                      }
                                    }}
                                    className="text-xs text-red-600 hover:text-red-800"
                                    title="Delete subscription and history"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                              {/* Last Sent Time */}
                              {sub.lastSentAt && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Last sent: {new Date(sub.lastSentAt.toDate ? sub.lastSentAt.toDate() : sub.lastSentAt).toLocaleString(undefined, { 
                                    timeZone: userTimezone || 'America/Phoenix',
                                    dateStyle: 'short',
                                    timeStyle: 'short'
                                  })} {userTimezone && `(${userTimezone.split('/')[1]})`}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Add Variant button for reports that support date ranges */}
                      {report.supportsDateRange && hasSubscriptions && (
                        <button
                          onClick={() => {
                            setReportCustomStates(prev => ({
                              ...prev,
                              [report.id]: { 
                                show: true, 
                                schedule: defaultSchedule, 
                                isNewVariant: true 
                              }
                            }));
                          }}
                          disabled={savingSubscription}
                          className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 font-medium"
                          title="Add another subscription with different date range"
                        >
                          ➕ Add Variant
                        </button>
                      )}
                      {/* Add first subscription button */}
                      {!hasSubscriptions && (
                        <button
                          onClick={() => {
                            setReportCustomStates(prev => ({
                              ...prev,
                              [report.id]: { 
                                show: true, 
                                schedule: defaultSchedule, 
                                isNewVariant: true 
                              }
                            }));
                          }}
                          disabled={savingSubscription}
                          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                          title="Subscribe to this report"
                        >
                          ➕ Subscribe
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {showReportCustom && (
                    <div className={`ml-4 p-3 border rounded space-y-2 ${editingSubscription ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}>
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        {editingSubscription ? '✏️ Edit Subscription' : reportCustomStates[report.id]?.isNewVariant ? '➕ Add New Variant' : '📅 Set Custom Schedule'}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Cadence</label>
                          <select
                            value={reportCustomSchedule.cadence}
                            onChange={(e) => {
                              const cadence = e.target.value;
                              setReportCustomStates(prev => ({
                                ...prev,
                                [report.id]: {
                                  ...prev[report.id],
                                  schedule: { ...reportCustomSchedule, cadence }
                                }
                              }));
                            }}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            {!isMonthlyOnlyReport(report.id) && <option value="daily">Daily</option>}
                            {!isMonthlyOnlyReport(report.id) && <option value="weekly">Weekly</option>}
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        {reportCustomSchedule.cadence === 'weekly' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Day of Week</label>
                            <select
                              value={reportCustomSchedule.day}
                              onChange={(e) => setReportCustomStates(prev => ({
                                ...prev,
                                [report.id]: {
                                  ...prev[report.id],
                                  schedule: { ...reportCustomSchedule, day: e.target.value }
                                }
                              }))}
                              className="border rounded px-2 py-1 text-sm"
                            >
                              <option value="monday">Monday</option>
                              <option value="tuesday">Tuesday</option>
                              <option value="wednesday">Wednesday</option>
                              <option value="thursday">Thursday</option>
                              <option value="friday">Friday</option>
                              <option value="saturday">Saturday</option>
                              <option value="sunday">Sunday</option>
                            </select>
                          </div>
                        )}
                        {reportCustomSchedule.cadence === 'monthly' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Day of Month</label>
                            <select
                              value={reportCustomSchedule.day || '1'}
                              onChange={(e) => setReportCustomStates(prev => ({
                                ...prev,
                                [report.id]: {
                                  ...prev[report.id],
                                  schedule: { ...reportCustomSchedule, day: e.target.value }
                                }
                              }))}
                              className="border rounded px-2 py-1 text-sm"
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                                <option key={day} value={day}>{day}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Time (15-min intervals)</label>
                          <input
                            type="time"
                            step="900"
                            value={reportCustomSchedule.time}
                            onChange={(e) => setReportCustomStates(prev => ({
                              ...prev,
                              [report.id]: {
                                ...prev[report.id],
                                schedule: { ...reportCustomSchedule, time: e.target.value }
                              }
                            }))}
                            className="border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        {/* Date Range Selector - only for reports that support it */}
                        {report.supportsDateRange && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">📅 Date Range</label>
                            <select
                              value={reportCustomSchedule.dateRange || 7}
                              onChange={(e) => setReportCustomStates(prev => ({
                                ...prev,
                                [report.id]: {
                                  ...prev[report.id],
                                  schedule: { ...reportCustomSchedule, dateRange: Number(e.target.value) }
                                }
                              }))}
                              className="border rounded px-2 py-1 text-sm bg-indigo-50 border-indigo-300"
                            >
                              {DATE_RANGE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            if (editingSubscription) {
                              try {
                                const normalizedSchedule = normalizeScheduleForReport(report.id, reportCustomSchedule);
                                const subRef = doc(db, collectionPath('reportSubscriptions'), editingSubscription.id);
                                await updateDoc(subRef, {
                                  customSchedule: normalizedSchedule,
                                  dateRange: normalizedSchedule.dateRange || null,
                                  frequency: 'custom',
                                  timezone: userTimezone || 'America/New_York',
                                  updatedAt: serverTimestamp()
                                });
                                setToast({ type: 'success', message: `✅ Subscription updated successfully!` });
                                setEditingSubscription(null);
                                setReportCustomStates(prev => ({
                                  ...prev,
                                  [report.id]: { ...prev[report.id], show: false }
                                }));
                              } catch (err) {
                                console.error('Error updating subscription:', err);
                                setToast({ type: 'error', message: 'Failed to update subscription' });
                              }
                            } else {
                              const isNewVariant = reportCustomStates[report.id]?.isNewVariant;
                              if (isNewVariant) {
                                createSubscriptionVariant(report.id, reportCustomSchedule);
                              } else {
                                toggleReportSubscription(report.id, 'custom', reportCustomSchedule);
                              }
                              setReportCustomStates(prev => ({
                                ...prev,
                                [report.id]: { ...prev[report.id], show: false, isNewVariant: false }
                              }));
                            }
                          }}
                          disabled={savingSubscription}
                          className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {editingSubscription ? '💾 Update' : reportCustomStates[report.id]?.isNewVariant ? 'Add Variant' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingSubscription(null);
                            setReportCustomStates(prev => ({
                              ...prev,
                              [report.id]: { ...prev[report.id], show: false }
                            }));
                          }}
                          className="px-2 py-1 bg-gray-300 rounded text-xs hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {reportSubscriptions.length > 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-sm text-green-800">
              ✅ You have {reportSubscriptions.length} active report subscription{reportSubscriptions.length !== 1 ? 's' : ''}.
              Reports will be sent to <strong>{currentUser?.email}</strong>.
            </div>
          </div>
        )}
      </div>

      {/* Send History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                📜 Report Send History
                {selectedHistoryReport && ` - ${AVAILABLE_REPORTS.find(r => r.id === selectedHistoryReport)?.name}`}
              </h3>
              <button
                onClick={() => {
                  setShowHistory(false);
                  setSelectedHistoryReport(null);
                  setSendHistory([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
              {loadingHistory ? (
                <div className="text-center py-8 text-gray-500">Loading history...</div>
              ) : sendHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No send history found</div>
              ) : (
                <div className="space-y-2">
                  {sendHistory.map(entry => (
                    <div 
                      key={entry.id} 
                      className={`p-3 rounded-lg border ${
                        entry.status === 'success' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-lg ${entry.status === 'success' ? '' : '⚠️'}`}>
                              {entry.status === 'success' ? '✅' : '❌'}
                            </span>
                            <div>
                              <div className="font-medium text-gray-900">
                                {AVAILABLE_REPORTS.find(r => r.id === entry.reportId)?.name || entry.reportId}
                              </div>
                              <div className="text-sm text-gray-600">{entry.reportSubject}</div>
                            </div>
                          </div>
                          {entry.error && (
                            <div className="mt-2 text-xs text-red-700 bg-red-100 p-2 rounded">
                              <strong>Error:</strong> {entry.error}
                              {entry.metadata?.retries && (
                                <span className="ml-2">(Failed after {entry.metadata.retries} retries)</span>
                              )}
                            </div>
                          )}
                          {entry.metadata && (
                            <div className="mt-2 text-xs text-gray-500">
                              Schedule: {entry.metadata.frequency}
                              {entry.metadata.customSchedule && (
                                <span> - {entry.metadata.customSchedule.cadence} at {entry.metadata.customSchedule.time}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 text-right">
                          {new Date(entry.sentAt.toDate ? entry.sentAt.toDate() : entry.sentAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => fetchSendHistory(null)}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                {selectedHistoryReport ? '📊 Show All Reports' : 'Showing all reports'}
              </button>
              <button
                onClick={() => {
                  setShowHistory(false);
                  setSelectedHistoryReport(null);
                  setSendHistory([]);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportSubscriptionsPanel;
